// Single source of truth for plan quotas.
// Used by both client (for display + optimistic gating) and server (for enforcement).

import type { Tier } from "@/hooks/useSubscription";

// Capability keys — these strings are the primary key for usage_counters rows.
// Keep in sync with the server enforceQuota calls.
export const CAPABILITIES = {
  jd_save: "jd_save",
  match_score: "match_score",
  resume_generation: "resume_generation",
  cover_letter: "cover_letter",
  qa_answer: "qa_answer",
  resume_regeneration: "resume_regeneration",
  forwarded_email: "forwarded_email",
  rejection_insight: "rejection_insight",
  career_path: "career_path",
  interview_question: "interview_question",
  interview_rerecord: "interview_rerecord",
  interview_prep_pack: "interview_prep_pack",
  linkedin_scrape: "linkedin_scrape",
  autofill_run: "autofill_run",
  learning_item: "learning_item",
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

// `null` = unlimited
export type Limit = number | null;

export type QuotaMatrix = Record<Capability, Record<Tier, Limit>>;

export const QUOTAS: QuotaMatrix = {
  jd_save:              { free: 5,    starter: 25,   pro: null, premium_agentic: null },
  match_score:          { free: 5,    starter: 50,   pro: null, premium_agentic: null },
  resume_generation:    { free: 1,    starter: 5,    pro: 30,   premium_agentic: null },
  cover_letter:         { free: 1,    starter: 5,    pro: 30,   premium_agentic: null },
  qa_answer:            { free: 3,    starter: 25,   pro: 150,  premium_agentic: null },
  resume_regeneration:  { free: 0,    starter: 1,    pro: 3,    premium_agentic: null },
  forwarded_email:      { free: 10,   starter: 50,   pro: 500,  premium_agentic: null },
  rejection_insight:    { free: 3,    starter: 15,   pro: null, premium_agentic: null },
  career_path:          { free: 1,    starter: 3,    pro: 10,   premium_agentic: null },
  interview_question:   { free: 2,    starter: 5,    pro: 30,   premium_agentic: null },
  interview_rerecord:   { free: 0,    starter: 1,    pro: 2,    premium_agentic: null },
  interview_prep_pack:  { free: 1,    starter: 5,    pro: 30,   premium_agentic: null },
  linkedin_scrape:      { free: 3,    starter: 15,   pro: 60,   premium_agentic: null },
  autofill_run:         { free: 0,    starter: 0,    pro: 3,    premium_agentic: null },
  learning_item:        { free: 5,    starter: 25,   pro: null, premium_agentic: null },
};

// Human-readable labels for upgrade dialogs / usage UI.
export const CAPABILITY_LABELS: Record<Capability, string> = {
  jd_save: "Job descriptions saved",
  match_score: "Match scores",
  resume_generation: "Tailored resumes",
  cover_letter: "Cover letters",
  qa_answer: "Application Q&A answers",
  resume_regeneration: "Resume regenerations",
  forwarded_email: "Forwarded-email tracking",
  rejection_insight: "Rejection insights",
  career_path: "Career path maps",
  interview_question: "Interview questions",
  interview_rerecord: "Interview re-recordings",
  interview_prep_pack: "Interview prep packs",
  linkedin_scrape: "Interviewer LinkedIn lookups",
  autofill_run: "Autonomous autofill runs",
  learning_item: "Learning queue items",
};

export function getLimit(capability: Capability, tier: Tier): Limit {
  return QUOTAS[capability][tier];
}

export function isUnlimited(limit: Limit): limit is null {
  return limit === null;
}

export function isOverLimit(limit: Limit, used: number): boolean {
  if (isUnlimited(limit)) return false;
  return used >= limit;
}

// Minimum tier that grants more headroom than the user's current tier for this capability.
export function nextTierWithMore(capability: Capability, currentTier: Tier): Tier | null {
  const order: Tier[] = ["free", "starter", "pro", "premium_agentic"];
  const currentIdx = order.indexOf(currentTier);
  const currentLimit = QUOTAS[capability][currentTier];
  for (let i = currentIdx + 1; i < order.length; i++) {
    const t = order[i];
    const lim = QUOTAS[capability][t];
    if (isUnlimited(lim)) return t;
    if (typeof currentLimit === "number" && typeof lim === "number" && lim > currentLimit) return t;
  }
  return null;
}

export const TIER_LABELS: Record<Tier, string> = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  premium_agentic: "Premium Agentic",
};

export function formatLimit(limit: Limit): string {
  return isUnlimited(limit) ? "Unlimited" : String(limit);
}
